import React, { useMemo } from 'react';

import { CopyButton } from '@/components/ui/copy-button';

import { JsonTree } from './JsonTree';

interface PanelProps {
  title: string;
  value: unknown;
  emptyText: string;
}

const JsonPanel: React.FC<PanelProps> = ({ title, value, emptyText }) => {
  const text = useMemo(() => (value == null ? '' : JSON.stringify(value, null, 2)), [value]);
  return (
    <section className="rounded-md border border-border">
      <header className="flex items-center justify-between border-b border-border px-3 py-2">
        <h3 className="text-sm font-medium">{title}</h3>
        {text && <CopyButton value={text} label={title} />}
      </header>
      {text ? (
        <div className="max-h-96 overflow-auto px-3 py-2">
          <JsonTree value={value} />
        </div>
      ) : (
        <p className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</p>
      )}
    </section>
  );
};

interface SidePanelsProps {
  context: Record<string, unknown> | null;
  agenda: Record<string, unknown> | null;
  coveredTopics: string[];
  usage: Record<string, unknown> | null;
}

/** Latest call context, the agenda with the topics covered so far, and token usage. */
export const SidePanels: React.FC<SidePanelsProps> = ({
  context,
  agenda,
  coveredTopics,
  usage,
}) => {
  const agendaValue = useMemo(
    () =>
      agenda || coveredTopics.length > 0 ? { ...(agenda ?? {}), covered: coveredTopics } : null,
    [agenda, coveredTopics],
  );
  return (
    <div className="flex flex-col gap-3">
      <JsonPanel title="Context" value={context} emptyText="No context recorded." />
      <JsonPanel title="Agenda" value={agendaValue} emptyText="No agenda recorded." />
      <JsonPanel title="Usage" value={usage} emptyText="No token usage recorded." />
    </div>
  );
};
